import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Bot, User, Clock, Zap, ArrowRight, ExternalLink, Phone, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { loadUserDataCache, saveUserDataCache } from '../utils/userDataCache';

// Load initial callback form data from shared cache
const getInitialCallbackForm = () => {
  const cached = loadUserDataCache();
  return {
    nom: cached.nom || '',
    prenom: cached.prenom || '',
    email: cached.email || '',
    telephone: cached.telephone || '',
    creneau: '',
    message: ''
  };
};

const ChatbotAI = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [language, setLanguage] = useState('fr');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [messageCount, setMessageCount] = useState(0); // Compteur de messages utilisateur
  const [cookiesAccepted, setCookiesAccepted] = useState(false); // Vérification des cookies
  const [callbackForm, setCallbackForm] = useState(getInitialCallbackForm);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  
  const MAX_MESSAGES = 10; // Limite maximale de messages par utilisateur

  const API_BASE = API_BASE_URL;

  // Save callback form data to shared cache when it changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (callbackForm.nom || callbackForm.prenom || callbackForm.email || callbackForm.telephone) {
        saveUserDataCache({
          nom: callbackForm.nom,
          prenom: callbackForm.prenom,
          email: callbackForm.email,
          telephone: callbackForm.telephone
        });
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [callbackForm.nom, callbackForm.prenom, callbackForm.email, callbackForm.telephone]);

  // Configuration multilingue
  const languages = {
    fr: { flag: '🇫🇷', label: 'Français' },
    de: { flag: '🇩🇪', label: 'Deutsch' },
    en: { flag: '🇬🇧', label: 'English' },
    it: { flag: '🇮🇹', label: 'Italiano' },
    pt: { flag: '🇵🇹', label: 'Português' }
  };

  // Messages de bienvenue multilingues
  const welcomeMessages = {
    fr: {
      greeting: `Bonjour ! 👋 Je suis l'assistant virtuel d'ATOM Group SA.`,
      services: `Je peux vous aider avec :
💰 Optimisation fiscale & 3ème pilier
🏠 Financement hypothécaire 
📈 Investissements et gestion patrimoine
🏥 Assurances vie et complémentaires`,
      question: `Comment puis-je vous accompagner aujourd'hui ?`,
      suggestions: [
        "💰 Optimiser ma fiscalité",
        "🏠 Calculer ma capacité hypothécaire",
        "📅 Prendre rendez-vous gratuit",
        "📞 Être rappelé par un conseiller"
      ]
    },
    de: {
      greeting: `Guten Tag! 👋 Ich bin der virtuelle Assistent der ATOM Group SA.`,
      services: `Ich kann Ihnen helfen mit:
💰 Steueroptimierung & Säule 3
🏠 Hypothekenfinanzierung
📈 Investitionen und Vermögensverwaltung
🏥 Lebens- und Zusatzversicherungen`,
      question: `Wie kann ich Sie heute unterstützen?`,
      suggestions: [
        "💰 Steuersituation optimieren",
        "🏠 Hypothekenkapazität berechnen",
        "📅 Kostenlosen Termin buchen",
        "📞 Von Berater zurückgerufen werden"
      ]
    },
    en: {
      greeting: `Hello! 👋 I'm the virtual assistant of ATOM Group SA.`,
      services: `I can help you with:
💰 Tax optimization & pillar 3
🏠 Mortgage financing
📈 Investments and wealth management
🏥 Life and supplementary insurance`,
      question: `How can I assist you today?`,
      suggestions: [
        "💰 Optimize my taxes",
        "🏠 Calculate mortgage capacity",
        "📅 Book free appointment",
        "📞 Get a callback from advisor"
      ]
    },
    it: {
      greeting: `Buongiorno! 👋 Sono l'assistente virtuale di ATOM Group SA.`,
      services: `Posso aiutarti con:
💰 Ottimizzazione fiscale & pilastro 3
🏠 Finanziamento ipotecario
📈 Investimenti e gestione patrimonio
🏥 Assicurazioni vita e complementari`,
      question: `Come posso assisterti oggi?`,
      suggestions: [
        "💰 Ottimizzare la mia situazione fiscale",
        "🏠 Calcolare capacità ipotecaria",
        "📅 Prenotare appuntamento gratuito",
        "📞 Essere richiamato da consulente"
      ]
    },
    pt: {
      greeting: `Olá! 👋 Sou o assistente virtual da ATOM Group SA.`,
      services: `Posso ajudá-lo com:
💰 Otimização fiscal & pilar 3
🏠 Financiamento hipotecário
📈 Investimentos e gestão de patrimônio
🏥 Seguros de vida e complementares`,
      question: `Como posso ajudá-lo hoje?`,
      suggestions: [
        "💰 Otimizar minha situação fiscal",
        "🏠 Calcular capacidade hipotecária",
        "📅 Agendar consulta gratuita",
        "📞 Ser contatado por consultor"
      ]
    }
  };

  // Labels pour formulaire callback multilingue
  const callbackLabels = {
    fr: {
      title: '📞 Demande de rappel',
      description: 'Laissez-nous vos coordonnées et nous vous rappellerons.',
      nom: 'Nom',
      prenom: 'Prénom',
      email: 'Email',
      telephone: 'Téléphone',
      creneau: 'Créneau préféré',
      message: 'Message (optionnel)',
      submit: 'Envoyer la demande',
      cancel: 'Annuler',
      success: '✅ Demande envoyée ! Nous vous rappellerons rapidement.',
      slots: ['Matin (8h-12h)', 'Après-midi (13h-17h)', 'Soirée (17h-19h)', 'Flexible']
    },
    de: {
      title: '📞 Rückruf-Anfrage',
      description: 'Hinterlassen Sie uns Ihre Kontaktdaten und wir rufen Sie zurück.',
      nom: 'Nachname',
      prenom: 'Vorname',
      email: 'E-Mail',
      telephone: 'Telefon',
      creneau: 'Bevorzugter Zeitraum',
      message: 'Nachricht (optional)',
      submit: 'Anfrage senden',
      cancel: 'Abbrechen',
      success: '✅ Anfrage gesendet! Wir rufen Sie bald zurück.',
      slots: ['Morgen (8-12 Uhr)', 'Nachmittag (13-17 Uhr)', 'Abend (17-19 Uhr)', 'Flexibel']
    },
    en: {
      title: '📞 Callback Request',
      description: 'Leave us your details and we will call you back.',
      nom: 'Last Name',
      prenom: 'First Name',
      email: 'Email',
      telephone: 'Phone',
      creneau: 'Preferred time slot',
      message: 'Message (optional)',
      submit: 'Send Request',
      cancel: 'Cancel',
      success: '✅ Request sent! We will call you back shortly.',
      slots: ['Morning (8am-12pm)', 'Afternoon (1pm-5pm)', 'Evening (5pm-7pm)', 'Flexible']
    },
    it: {
      title: '📞 Richiesta di richiamata',
      description: 'Lasciaci i tuoi dati e ti richiameremo.',
      nom: 'Cognome',
      prenom: 'Nome',
      email: 'Email',
      telephone: 'Telefono',
      creneau: 'Fascia oraria preferita',
      message: 'Messaggio (facoltativo)',
      submit: 'Invia richiesta',
      cancel: 'Annulla',
      success: '✅ Richiesta inviata! Ti richiameremo presto.',
      slots: ['Mattina (8-12)', 'Pomeriggio (13-17)', 'Sera (17-19)', 'Flessibile']
    },
    pt: {
      title: '📞 Solicitação de retorno',
      description: 'Deixe-nos seus dados e entraremos em contato.',
      nom: 'Sobrenome',
      prenom: 'Nome',
      email: 'Email',
      telephone: 'Telefone',
      creneau: 'Horário preferido',
      message: 'Mensagem (opcional)',
      submit: 'Enviar solicitação',
      cancel: 'Cancelar',
      success: '✅ Solicitação enviada! Entraremos em contato em breve.',
      slots: ['Manhã (8h-12h)', 'Tarde (13h-17h)', 'Noite (17h-19h)', 'Flexível']
    }
  };

  // Générer un ID de session unique
  useEffect(() => {
    if (!sessionId) {
      setSessionId(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    }
    
    // Vérifier si les cookies ont été acceptés
    const checkCookieConsent = () => {
      const consent = localStorage.getItem('atomgroup-cookie-consent');
      if (consent) {
        try {
          const preferences = JSON.parse(consent);
          // Si au moins les cookies nécessaires sont acceptés ou tout est accepté
          if (preferences.necessary || preferences.analytics) {
            setCookiesAccepted(true);
          }
        } catch (e) {
          // En cas d'erreur, afficher le chatbot par défaut
          setCookiesAccepted(true);
        }
      } else {
        // Si aucun consentement n'est enregistré, afficher quand même le chatbot après 3 secondes
        setTimeout(() => setCookiesAccepted(true), 3000);
      }
    };
    
    checkCookieConsent();
    
    // Écouter les changements de consentement des cookies
    const handleStorageChange = (e) => {
      if (e.key === 'atomgroup-cookie-consent' && e.newValue) {
        checkCookieConsent();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Vérifier régulièrement (au cas où le banner est sur la même page)
    const interval = setInterval(checkCookieConsent, 2000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Scroll automatique vers le bas
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus sur input quand le chat s'ouvre
  useEffect(() => {
    if (isOpen && inputRef.current && !showCallbackForm) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, showCallbackForm]);

  // Message de bienvenue initial
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcome = welcomeMessages[language];
      const welcomeMessage = {
        id: 'welcome',
        type: 'bot',
        content: `${welcome.greeting}\n\n${welcome.services}\n\n${welcome.question}`,
        timestamp: new Date(),
        suggestions: welcome.suggestions
      };
      setMessages([welcomeMessage]);
      setSuggestions(welcome.suggestions);
    }
  }, [isOpen, messages.length, language]);

  // Changer de langue
  const changeLanguage = (newLang) => {
    setLanguage(newLang);
    setShowLanguageMenu(false);
    
    // Mettre à jour TOUS les messages du bot dans la nouvelle langue
    const welcome = welcomeMessages[newLang];
    const welcomeMessage = {
      id: 'welcome',
      type: 'bot',
      content: `${welcome.greeting}\n\n${welcome.services}\n\n${welcome.question}`,
      timestamp: new Date(),
      suggestions: welcome.suggestions
    };
    
    // Remplacer tous les messages par le nouveau message de bienvenue
    // (efface l'historique pour éviter un mélange de langues)
    setMessages([welcomeMessage]);
    setSuggestions(welcome.suggestions);
    setInputMessage('');
    setShowCallbackForm(false);
  };

  // Envoyer un message
  const sendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim() || isLoading) return;

    // Vérifier la limite de messages
    if (messageCount >= MAX_MESSAGES) {
      const limitMessage = {
        id: `limit_${Date.now()}`,
        type: 'bot',
        content: language === 'fr' 
          ? `⚠️ Vous avez atteint la limite de ${MAX_MESSAGES} messages pour cette session. Pour continuer la conversation, veuillez nous contacter directement via notre formulaire de contact ou par email à contact@atom-group.ch.`
          : language === 'de'
          ? `⚠️ Sie haben die Grenze von ${MAX_MESSAGES} Nachrichten für diese Sitzung erreicht. Um das Gespräch fortzusetzen, kontaktieren Sie uns bitte direkt über unser Kontaktformular oder per E-Mail an contact@atom-group.ch.`
          : language === 'en'
          ? `⚠️ You have reached the limit of ${MAX_MESSAGES} messages for this session. To continue the conversation, please contact us directly via our contact form or by email at contact@atom-group.ch.`
          : language === 'it'
          ? `⚠️ Hai raggiunto il limite di ${MAX_MESSAGES} messaggi per questa sessione. Per continuare la conversazione, contattaci direttamente tramite il nostro modulo di contatto o via email a contact@atom-group.ch.`
          : `⚠️ Você atingiu o limite de ${MAX_MESSAGES} mensagens para esta sessão. Para continuar a conversa, entre em contato conosco diretamente através do nosso formulário de contato ou por email em contact@atom-group.ch.`,
        timestamp: new Date(),
        isLimit: true
      };
      setMessages(prev => [...prev, limitMessage]);
      return;
    }

    const userMessage = {
      id: `user_${Date.now()}`,
      type: 'user',
      content: messageText.trim(),
      timestamp: new Date()
    };

    // Ajouter le message utilisateur
    setMessages(prev => [...prev, userMessage]);
    setMessageCount(prev => prev + 1); // Incrémenter le compteur
    setInputMessage('');
    setIsLoading(true);
    setIsTyping(true); // Afficher l'indicateur de frappe
    setSuggestions([]);

    try {
      // Envoyer à l'API chatbot avec la langue
      const response = await axios.post(`${API_BASE}/chatbot/message`, {
        message: messageText.trim(),
        session_id: sessionId,
        language: language,
        user_info: {
          page: window.location.pathname,
          timestamp: new Date().toISOString()
        }
      });

      if (response.data.success) {
        // Simuler un délai de frappe pour UX naturelle
        await new Promise(resolve => setTimeout(resolve, 800));
        
        const botMessage = {
          id: `bot_${Date.now()}`,
          type: 'bot',
          content: response.data.response,
          timestamp: new Date(),
          suggestions: response.data.suggestions || [],
          leadQualified: response.data.lead_qualified
        };

        setMessages(prev => [...prev, botMessage]);
        setSuggestions(response.data.suggestions || []);

        // Détecter si callback demandé
        if (response.data.callback_requested) {
          setShowCallbackForm(true);
        }

        // Gestion de la redirection automatique
        if (response.data.redirect_url) {
          const redirectMessage = {
            id: `redirect_${Date.now()}`,
            type: 'bot',
            content: `🔗 Je vous redirige vers notre formulaire de contact pour prendre rendez-vous.`,
            timestamp: new Date(),
            isRedirect: true,
            redirectUrl: response.data.redirect_url
          };
          
          setTimeout(() => {
            setMessages(prev => [...prev, redirectMessage]);
            
            // Redirection automatique après 3 secondes
            setTimeout(() => {
              setShowLanguageMenu(false); // Fermer le menu langue si ouvert
              setIsOpen(false);
              navigate(response.data.redirect_url);
            }, 3000);
          }, 1000);
        }

        // Analytics tracking
        if (typeof gtag !== 'undefined') {
          gtag('event', 'chatbot_interaction', {
            session_id: sessionId,
            language: language,
            message_length: messageText.length,
            lead_qualified: response.data.lead_qualified,
            callback_requested: response.data.callback_requested,
            redirect_triggered: !!response.data.redirect_url
          });
        }
      } else {
        // Message d'erreur fallback
        const errorMessage = {
          id: `error_${Date.now()}`,
          type: 'bot',
          content: response.data.fallback_response || "Désolé, je rencontre un problème technique. Vous pouvez nous contacter directement via contact@atom-group.ch.",
          timestamp: new Date(),
          isError: true
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Erreur chatbot:', error);
      const errorMessage = {
        id: `error_${Date.now()}`,
        type: 'bot',
        content: "Je rencontre des difficultés de connexion. N'hésitez pas à nous contacter directement via contact@atom-group.ch",
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsTyping(false); // Désactiver l'indicateur de frappe
    }
  };

  // Envoyer le formulaire de callback
  const sendCallbackRequest = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE}/chatbot/callback`, {
        nom: callbackForm.nom,
        prenom: callbackForm.prenom,
        email: callbackForm.email,
        telephone: callbackForm.telephone,
        creneau: callbackForm.creneau,
        message: callbackForm.message,
        session_id: sessionId,
        language: language
      });

      if (response.data.success) {
        // Message de succès
        const successMessage = {
          id: `callback_success_${Date.now()}`,
          type: 'bot',
          content: callbackLabels[language].success,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, successMessage]);
        
        // Réinitialiser le formulaire
        setShowCallbackForm(false);
        setCallbackForm({
          nom: '',
          prenom: '',
          email: '',
          telephone: '',
          creneau: '',
          message: ''
        });

        // Analytics
        if (typeof gtag !== 'undefined') {
          gtag('event', 'callback_request_submitted', {
            session_id: sessionId,
            language: language
          });
        }
      }
    } catch (error) {
      console.error('Erreur callback:', error);
      const errorMessage = {
        id: `error_${Date.now()}`,
        type: 'bot',
        content: "Erreur lors de l'envoi de la demande. Veuillez réessayer.",
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Envoyer une suggestion
  const sendSuggestion = (suggestion) => {
    sendMessage(suggestion);
  };

  // Gérer l'envoi par Enter
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Formater le timestamp
  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('fr-CH', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <>
      {/* Bouton flottant - Affiché seulement si les cookies sont acceptés */}
      {!isOpen && cookiesAccepted && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-4 md:bottom-6 md:right-6 z-[60] w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 hover:from-blue-600 hover:via-blue-700 hover:to-blue-800 text-white rounded-full shadow-2xl hover:shadow-blue-500/50 transform hover:scale-110 hover:rotate-6 transition-all duration-300 flex items-center justify-center group animate-bounce-subtle"
          data-testid="chatbot-toggle-open"
          aria-label="Ouvrir le chat"
        >
          <MessageCircle size={24} className="md:scale-110 group-hover:scale-125 transition-transform" />
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/50"></div>
          
          {/* Tooltip amélioré */}
          <div className="hidden md:block absolute bottom-full right-0 mb-3 px-4 py-2 bg-gray-900 border border-gray-700 text-white text-sm rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap shadow-xl backdrop-blur-sm transform group-hover:-translate-y-1 pointer-events-none">
            💬 Besoin d'aide ? Chattez avec nous !
            <div className="absolute -bottom-1 right-4 w-2 h-2 bg-gray-900 border-r border-b border-gray-700 transform rotate-45"></div>
          </div>
        </button>
      )}

      {/* Backdrop pour fermer sur mobile */}
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-[55] md:hidden"
            onClick={(e) => {
              e.stopPropagation();
              setShowLanguageMenu(false); // Fermer le menu langue si ouvert
              setIsOpen(false);
            }}
            aria-label="Fermer le chat"
          ></div>
          
          {/* Interface de chat */}
          <div 
            className="fixed bottom-0 right-0 md:bottom-6 md:right-6 z-[60] w-full md:w-96 h-[70vh] md:h-auto md:max-h-[600px] bg-gray-900/95 backdrop-blur-lg border-t md:border border-gray-700 rounded-t-3xl md:rounded-3xl shadow-2xl flex flex-col overflow-hidden" 
            data-testid="chatbot-window"
            onClick={(e) => {
              // Fermer le menu langue si on clique ailleurs dans le chatbot
              if (showLanguageMenu && !e.target.closest('[data-testid="language-selector"]')) {
                setShowLanguageMenu(false);
              }
            }}
          >
          
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Assistant ATOM</h3>
                <div className="flex items-center text-green-300 text-xs">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                  En ligne
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* Sélecteur de langue */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLanguageMenu(!showLanguageMenu);
                  }}
                  className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-full transition-all flex items-center"
                  data-testid="language-selector"
                >
                  <Globe size={18} />
                  <span className="ml-1 text-sm">{languages[language].flag}</span>
                </button>
                
                {showLanguageMenu && (
                  <>
                    {/* Backdrop invisible pour fermer le menu */}
                    <div 
                      className="fixed inset-0 z-[59]" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowLanguageMenu(false);
                      }}
                    />
                    <div className="absolute top-full right-0 mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-1 z-[70]">
                      {Object.entries(languages).map(([code, { flag, label }]) => (
                        <button
                          key={code}
                          onClick={(e) => {
                            e.stopPropagation();
                            changeLanguage(code);
                          }}
                          className={`w-full px-4 py-2 text-left hover:bg-gray-700 text-white text-sm flex items-center space-x-2 ${
                            language === code ? 'bg-gray-700' : ''
                          }`}
                          data-testid={`language-${code}`}
                        >
                          <span className="text-lg">{flag}</span>
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLanguageMenu(false); // Fermer le menu langue si ouvert
                  setIsOpen(false);
                }}
                className="text-white hover:text-white hover:bg-white/30 p-2 rounded-full transition-all z-[100] relative"
                data-testid="chatbot-toggle-close"
                aria-label="Fermer le chat"
              >
                <X size={20} strokeWidth={2.5} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-gradient-to-b from-gray-900 to-gray-800">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[85%] ${
                  message.type === 'user'
                    ? 'bg-blue-500 text-white rounded-2xl rounded-br-md'
                    : message.isError 
                      ? 'bg-red-500/20 border border-red-500/50 text-red-300 rounded-2xl rounded-bl-md'
                      : 'glass text-white rounded-2xl rounded-bl-md'
                } p-3 shadow-lg`}>
                  
                  <div className="flex items-start space-x-2 mb-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.type === 'user' 
                        ? 'bg-white/20' 
                        : message.isError
                          ? 'bg-red-500/30'
                          : 'bg-blue-500/30'
                    }`}>
                      {message.type === 'user' ? (
                        <User size={14} />
                      ) : message.isError ? (
                        <X size={14} />
                      ) : (
                        <Bot size={14} />
                      )}
                    </div>
                    <div className="text-xs opacity-70 mt-1">
                      {formatTime(message.timestamp)}
                    </div>
                  </div>
                  
                  <div className="ml-8 whitespace-pre-wrap text-sm leading-relaxed">
                    {message.content}
                  </div>

                  {/* Badge lead qualifié */}
                  {message.leadQualified && (
                    <div className="ml-8 mt-2 inline-flex items-center px-2 py-1 bg-green-500/20 border border-green-400/30 rounded-full text-xs text-green-300">
                      <Zap size={10} className="mr-1" />
                      Lead qualifié
                    </div>
                  )}

                  {/* Badge redirection */}
                  {message.isRedirect && (
                    <div className="ml-8 mt-2 inline-flex items-center px-2 py-1 bg-blue-500/20 border border-blue-400/30 rounded-full text-xs text-blue-300">
                      <ExternalLink size={10} className="mr-1" />
                      Redirection dans 3s...
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Indicateur de frappe */}
            {isTyping && !showCallbackForm && (
              <div className="flex justify-start animate-fade-in">
                <div className="glass p-3 rounded-2xl rounded-bl-md shadow-lg">
                  <div className="flex items-center space-x-2">
                    <Bot size={16} className="text-blue-300" />
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.15s'}}></div>
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.3s'}}></div>
                    </div>
                    <span className="text-xs text-gray-300 animate-pulse">Assistant réfléchit...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Formulaire de callback */}
          {showCallbackForm && (
            <div className="px-4 py-3 border-t border-gray-700 bg-gray-800/80">
              <form onSubmit={sendCallbackRequest} className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white font-semibold text-sm flex items-center">
                    <Phone size={16} className="mr-2 text-blue-400" />
                    {callbackLabels[language].title}
                  </h4>
                  <button
                    type="button"
                    onClick={() => setShowCallbackForm(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X size={16} />
                  </button>
                </div>
                <p className="text-xs text-gray-400 mb-3">{callbackLabels[language].description}</p>
                
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder={callbackLabels[language].prenom}
                    value={callbackForm.prenom}
                    onChange={(e) => setCallbackForm({...callbackForm, prenom: e.target.value})}
                    required
                    className="bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    data-testid="callback-prenom"
                  />
                  <input
                    type="text"
                    placeholder={callbackLabels[language].nom}
                    value={callbackForm.nom}
                    onChange={(e) => setCallbackForm({...callbackForm, nom: e.target.value})}
                    required
                    className="bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    data-testid="callback-nom"
                  />
                </div>
                
                <input
                  type="email"
                  placeholder={callbackLabels[language].email}
                  value={callbackForm.email}
                  onChange={(e) => setCallbackForm({...callbackForm, email: e.target.value})}
                  required
                  className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  data-testid="callback-email"
                />
                
                <input
                  type="tel"
                  placeholder={callbackLabels[language].telephone}
                  value={callbackForm.telephone}
                  onChange={(e) => setCallbackForm({...callbackForm, telephone: e.target.value})}
                  required
                  className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  data-testid="callback-telephone"
                />
                
                <select
                  value={callbackForm.creneau}
                  onChange={(e) => setCallbackForm({...callbackForm, creneau: e.target.value})}
                  required
                  className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-xs focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  data-testid="callback-creneau"
                >
                  <option value="">{callbackLabels[language].creneau}</option>
                  {callbackLabels[language].slots.map((slot, index) => (
                    <option key={index} value={slot}>{slot}</option>
                  ))}
                </select>
                
                <textarea
                  placeholder={callbackLabels[language].message}
                  value={callbackForm.message}
                  onChange={(e) => setCallbackForm({...callbackForm, message: e.target.value})}
                  rows="2"
                  className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                  data-testid="callback-message"
                />
                
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowCallbackForm(false)}
                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-xs font-medium transition-all"
                  >
                    {callbackLabels[language].cancel}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center"
                    data-testid="callback-submit"
                  >
                    {isLoading ? (
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    ) : (
                      <>
                        <Send size={14} className="mr-1" />
                        {callbackLabels[language].submit}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Suggestions rapides */}
          {!showCallbackForm && suggestions.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-700 bg-gray-800/30">
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => sendSuggestion(suggestion)}
                    className="group text-xs bg-gradient-to-r from-blue-500/20 to-blue-600/20 hover:from-blue-500/40 hover:to-blue-600/40 text-blue-300 hover:text-white px-3 py-2 rounded-full border border-blue-500/30 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 flex items-center transform hover:scale-105 hover:-translate-y-0.5"
                    data-testid={`suggestion-btn-${index}`}
                  >
                    <span className="font-medium">{suggestion}</span>
                    <ArrowRight size={12} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          {!showCallbackForm && (
            <div className="p-4 border-t border-gray-700 bg-gray-800/50">
              <div className="flex space-x-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Tapez votre question..."
                  className="flex-1 bg-gray-700/50 border border-gray-600 rounded-full px-4 py-2 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm"
                  disabled={isLoading}
                  data-testid="chatbot-input"
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!inputMessage.trim() || isLoading}
                  className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-full flex items-center justify-center transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/50 transform hover:scale-110 active:scale-95"
                  data-testid="chatbot-send-btn"
                >
                  <Send size={16} className={isLoading ? "animate-pulse" : ""} />
                </button>
              </div>
              
              {/* Footer */}
              <div className="text-center mt-2 text-xs text-gray-500">
                Powered by ATOM Group SA • 
                <Clock size={10} className="inline mx-1" />
                Réponse rapide garantie
              </div>
            </div>
          )}
        </div>
        </>
      )}
    </>
  );
};

export default ChatbotAI;